// pa-margins.ts — Target purchase price, margin slope, real supplier purchase prices
// (book REGLES_DEVIS, "target PA and margin degradation" + "supplier PA — atomic keys").
//
// Target PA (book 4.6) = tier net PV × (1 − tier target margin), where tier net PV = full × (1 − palTaux).
// The target margin is read from margin_tiers (60/57.5/55/52.5/50/45 %), so the PA hits that target
// EXACTLY. The net PV behind the PA is the TIER net only — never agency, special or client rate table.
//
// Real supplier PA: atomic key material × printing × cutting × lamination × finishing, per provider ×
// mode (INCLUDED/SUPPLIED), with a cutting equivalence class, finishing tolerance, the Prodhuit 1 m²
// minimum, and a 5 %/element floor on the target PA side. A manual PA always wins.
//
// Ported from compute() of prototype v13.7.

import {
  WORKSHOP_MINIMUMS,
  PA_FLAT_FEE_PCT,
  COMBINATIONS,
  PA_SUPPLIERS,
  PA_NOTES,
  SERVICE_KEY,
  CUT_CLASS_PA,
} from "../../data";
import { type QuoteLine, num, round2, isFilled, findMat } from "../core/core";
import { isLargeFormatPrintRegime } from "../core/regimes";
import { marginTier, type VolumeGrid } from "./volume-tiers";
import { isProvisionalRef } from "../catalogue/provisional-material";
import type { Combination, PaSupplier } from "../../models/data.model";

// Target PA & margin

/**
 * Target PA €/m² (book 4.6): reference cost set to hit the volume tier's target margin EXACTLY.
 * `PA = tier net PV × (1 − target margin)`, where `tier net PV = full × (1 − palTaux)` and `targetMargin`
 * is read from margin_tiers (60/57.5/55/52.5/50/45 %).
 *
 * The "net PV" behind the PA is STRICTLY the VOLUME TIER net: never the agency discount, never the
 * top-of-table special, never a client rate table ("the PA only sees the tier"). `palTaux` and
 * `targetMargin` depend on volume alone, so the PA is client-invariant by construction.
 *
 * Replaces the old 4.5 formula `full × 0.40 × (1 − palTaux × 2/3)`. Both agree at tier 0 (full × 0.40,
 * 60 % margin); the gap widens with the tier, and the table is now authoritative.
 * @param fullBase The full (catalogue) price per m².
 * @param palTaux The volume tier discount rate.
 * @param targetMargin The target margin of the tier.
 * @returns The target PA per m².
 */
export function targetPaM2(fullBase: number, palTaux: number, targetMargin: number): number {
  return fullBase * (1 - palTaux) * (1 - targetMargin);
}

/**
 * Total target PA of the line, with the 5 % FLOOR per element: each piece is costed on at least
 * 0.05 m² — it only bites below 500 cm². A hand-entered PA (`manual`) is never adjusted: it applies to
 * the real area.
 * @param params The PA per m², the unit area, the quantity, the total area and whether the PA was hand-entered.
 * @returns The total target PA, rounded to the cent.
 */
export function targetPaTotal(params: {
  paM2: number;
  m2unit: number;
  qty: number;
  m2total: number;
  manual: boolean;
}): number {
  const { paM2, m2unit, qty, m2total, manual } = params;
  const total =
    !manual && m2unit > 0 && qty > 0 ? paM2 * Math.max(m2unit, 0.05) * qty : paM2 * m2total;
  return round2(total);
}

/**
 * Margin in % (1 decimal): 1 − PA/PV. By convention, 60 % when the PV is zero (empty line).
 * @param pv The sale price.
 * @param pa The purchase price.
 * @returns The margin in percent.
 */
export function marginPct(pv: number, pa: number): number {
  return pv > 0 ? Math.round((1 - pa / pv) * 1000) / 10 : 60;
}

/**
 * Negotiated €/m², unit price and margin from a purchase TOTAL typed on a line: each one follows the total as soon as
 * what it needs is there (a surface for the €/m², a quantity for the €/unit, a sale price for the margin).
 * @param total The typed purchase total.
 * @param quantity The quantity of the line.
 * @param pvNet The net sale price of the line.
 * @param m2 The total surface of the line.
 * @returns The €/m², the €/unit and the margin %, each null when it cannot be computed.
 */
export function negotiatedFromTotal(
  total: number,
  quantity: number,
  pvNet: number,
  m2: number,
): { paM2: number | null; paU: number | null; marginPct: number | null } {
  return {
    paM2: m2 > 0 ? round2(total / m2) : null,
    paU: quantity > 0 ? round2(total / quantity) : null,
    marginPct: realMarginPct(pvNet, total),
  };
}

/**
 * Real margin on the supplier cost; null when the net PV is zero.
 * @param pvNet The net sale price.
 * @param realPa The real supplier purchase price.
 * @returns The margin in percent, or null.
 */
export function realMarginPct(pvNet: number, realPa: number): number | null {
  return pvNet > 0 ? Math.round((1 - realPa / pvNet) * 1000) / 10 : null;
}

/**
 * Target PA of a minimum flat fee: PA_FLAT_FEE_PCT of the fee (40 % margin); an entered PA wins.
 * @param flatFee The flat fee amount.
 * @param override A PA entered by hand, if any.
 * @returns The PA of the flat fee.
 */
export function flatFeePa(flatFee: number, override?: unknown): number {
  return isFilled(override) ? num(override) : round2(flatFee * PA_FLAT_FEE_PCT);
}

/**
 * Effective discount of a forced-price line: real gap between the full catalogue price and the forced
 * price (negative when it is a markup). Null when the full price is zero, so the displayed % follows
 * the forcing.
 * @param pvForce The forced price.
 * @param pvPlein The full catalogue price.
 * @returns The effective discount as a fraction, or null.
 */
export function effectiveDiscount(pvForce: number, pvPlein: number): number | null {
  return pvPlein > 0 ? 1 - pvForce / pvPlein : null;
}

// Combination recognition

/**
 * Reference of the combination matching the line's CURRENT composition (finishings unordered); empty
 * string when none. Strict match first; if the cutting belongs to the PA equivalence class, a canonical
 * cutting of the same class is tolerated (the PV still follows the real cutting).
 * @param l The quote line.
 * @returns The combination reference, or an empty string.
 */
export function detectCombi(l: QuoteLine): string {
  if (!isLargeFormatPrintRegime(l) || !l.material) return "";
  /**
   * Loose string equality, treating null/undefined as empty.
   * @param a The first value.
   * @param b The second value.
   * @returns True when the string forms are equal.
   */
  const eq = (a: unknown, b: unknown) => String(a ?? "") === String(b ?? "");
  const confs = [l.finishing1 ?? "", l.finishing2 ?? ""].sort().join("|");
  /**
   * First combination matching the line with the given cut.
   * @param cut The cut code to match.
   * @returns The matching combination, or undefined.
   */
  const match = (cut: string) =>
    COMBINATIONS.find(
      (c) =>
        eq(c.material, l.material) &&
        eq(c.print, l.print) &&
        eq(c.cut, cut) &&
        eq(c.lamination, l.lamination) &&
        [c.finishing1 || "", c.finishing2 || ""].sort().join("|") === confs,
    );
  let hit = l.cut != null ? match(String(l.cut)) : undefined;
  if (!hit && l.cut && CUT_CLASS_PA.includes(l.cut)) {
    for (const c of CUT_CLASS_PA) {
      if (c !== l.cut) {
        hit = match(c);
        if (hit) break;
      }
    }
  }
  // Every detected config carries a combination ref of its own: the 158 standard combos, once keyed on
  // the material ref ("AD-101"), were given a CB- ref. The combination ref column therefore NEVER holds a
  // material ref — a recognised config yields its combination ref (CB- for the standard one, a dedicated
  // ref such as "AD-101-LX" for a variant), otherwise empty. The real PA matches the component TUPLE
  // (paSupplierLookup), independently of this ref.
  return hit ? hit.ref : "";
}

/**
 * INVERSE of detectCombi: the components of a combination from its reference. Used for data entry —
 * picking a ref in the combination ref column prefills the line's material, print, cutting, lamination and
 * finishings. Returns undefined when the ref is unknown.
 * @param ref The combination reference.
 * @returns The combination, or undefined.
 */
export function combiComponents(ref: string): Combination | undefined {
  if (!ref) return undefined;
  return COMBINATIONS.find((c) => c.ref === ref);
}

// Real supplier PA (provider × mode)

/** PA mode: material included in the workshop PA, or supplied by UPSIZE. */
export type PaMode = "INCLUDED" | "SUPPLIED";

/**
 * PA mode of the line. The material is SUPPLIED ONLY if it is in-house stock UPZ (catalogue flag, auto)
 * OR the supplied-material checkbox is on (`materialSupplied`, the only MANUAL trigger) OR a real purchase is
 * entered. The material SUPPLIER (`matSupplier`) and the BRAND trigger nothing — purely indicative. No
 * checkbox = neutral INCLUDED state.
 * @param l The quote line.
 * @param enteredPurchase Whether a real purchase was entered for the material.
 * @returns The PA mode.
 */
export function paMode(l: QuoteLine, enteredPurchase = false): PaMode {
  const materialEntry = findMat(l.material);
  const supplied = !!(materialEntry?.stockUpz || l.materialSupplied || enteredPurchase);
  return supplied ? "SUPPLIED" : "INCLUDED";
}

/** Result of a supplier PA lookup. */
export interface PaSupplierResult {
  /** Provider's PA €/m² for this exact combination. */
  pa: number;
  /** Associated note (PA_NOTES) where applicable. */
  note: string;
  /** true when the P8 note requiring validation with Odillon applies. */
  odillon: boolean;
  /** true when the finishing was ignored to find a match. */
  finishingIgnored: boolean;
  /** Minimum purchase flat fee carried by the matched key (€), if any. Consolidated PER GROUP. */
  min_flat_fee?: number;
}

/**
 * INJECTED PA rate tables: the purchase keys the lookup consults (workshop × key × mode, 6 tiers).
 * Default = live runtime `PA_SUPPLIERS` (bound / patched from Supabase), identical to pre-injection
 * behaviour once data is loaded. The application passes the database override through the SAME
 * pattern as the catalogue: embedded structure, database prices. The engine no longer DECIDES where
 * PAs come from, it RECEIVES them.
 */
export type PaRateCards = readonly PaSupplier[];

/**
 * Live default PA tables — always the currently bound `PA_SUPPLIERS` (not a snapshot taken at
 * module load, which would stay empty after `patchPaSuppliers` / `bindEngineData`).
 */
export { PA_SUPPLIERS as EMBEDDED_PA_GRIDS } from "../../data";

/** Volume context of the lookup: without it, tier 0 (the smallest volume) is returned. */
export interface PaSupplierOptions {
  /** Cumulated m² of the material on the quote — the SAME one as the sale tiers (a single truth). */
  cumulatedM2?: number;
  /** Injected MARGIN_TIERS_SEED table: it carries the thresholds, hence the purchase tiers. */
  grid?: VolumeGrid;
  /** Injected PA tables (workshop × key). Absent = embedded — never a gap, never an invented price. */
  paGrids?: PaRateCards;
}

/**
 * Applicable PA tier: the `sort_order` (0..5) of the volume tier, read from the INJECTED table. PURCHASE
 * tiers follow SALE tiers — target PA, target margin and volume discount share the same bounds, and a
 * tier bump must move them TOGETHER. Strict bound inherited from marginTier: 10 m² exactly → tier
 * 10-49.999, as on the sale side. Without context → tier 0: never an assumed bulk price.
 * @param opts The lookup options carrying the volume context.
 * @param pa The six tier prices of the matched key.
 * @returns The PA of the applicable tier.
 */
function tierOf(opts: PaSupplierOptions | undefined, pa: readonly number[]): number {
  const g = opts?.grid;
  const i = g ? marginTier(opts?.cumulatedM2 ?? 0, g).sort_order : 0;
  return pa[i] ?? pa[0] ?? 0; // table longer than the price list: never go past the last one
}

// Finishings that DEFINE a supplier PA key of their own (the PA changes with them): the INS double
// process (CONF-1601/1638) and the KANDA textile finishings — frame beading (CONF-1603) and the
// special-finishing trio lead cord / female velcro / sleeve (CONF-1606/1640/1634, same PA). Any
// other finishing is PA-neutral (key finishingKey="" with `finishingIgnored`).
const CF_PA_KEY = new Set(["CONF-1601", "CONF-1638", "CONF-1603", "CONF-1606", "CONF-1640", "CONF-1634"]);

/**
 * Index key for PA grid lookup (excludes mode — checked on candidates).
 * @param f The workshop key.
 * @param m The material reference ("" for a generic key).
 * @param i The print mode.
 * @param c The cut.
 * @param p The lamination.
 * @param finishingKey The priced finishing.
 * @returns The composite index key.
 */
function paIndexKey(
  f: string,
  m: string,
  i: string,
  c: string,
  p: string,
  finishingKey: string,
): string {
  return `${f}\0${m}\0${i}\0${c}\0${p}\0${finishingKey}`;
}

/** Cached indexes keyed by the grids array identity. */
const paIndexCache = new WeakMap<object, Map<string, PaSupplier[]>>();

/**
 * Placeholder rows from `preparePaGridKeys` are inserted as `[0,0,0,0,0,0]`.
 * Treat all-zero tiers as incomplete so menus / coverage / pricing do not treat them as a real grid.
 * @param pa The six tier prices of a key.
 * @returns True when at least one tier is non-zero.
 */
function isCollectedPaGrid(pa: readonly number[] | null | undefined): boolean {
  return !!pa?.some((v) => Number(v) !== 0);
}

/**
 * Indexes the PA grids by atomic key, cached on the grids array identity.
 * @param grids The PA tables.
 * @returns The index from composite key to candidate rows.
 */
function indexPaGrids(grids: readonly PaSupplier[]): Map<string, PaSupplier[]> {
  const cacheKey = grids as unknown as object;
  const hit = paIndexCache.get(cacheKey);
  if (hit) return hit;
  const map = new Map<string, PaSupplier[]>();
  for (const x of grids) {
    const k = paIndexKey(x.workshop, x.material, x.print, x.cut, x.lamination, x.finishing);
    const bucket = map.get(k);
    if (bucket) bucket.push(x);
    else map.set(k, [x]);
  }
  paIndexCache.set(cacheKey, map);
  return map;
}

/**
 * Real supplier PA for a line and a given mode, or null when no key matches.
 *
 * Search order, most specific to most general:
 *  1. exact cutting, then canonical cutting CUT-1501 if the cutting is in the "no provider finishing" class;
 *  2. PRICED finishing (it defines a PA key of its own) exact, then ignored (`finishingIgnored`);
 *  3. EXPLICIT MATERIAL key, then GENERIC key (empty material).
 *
 * EXPLICIT > GENERIC (book §18): a workshop can price by process ("all materials, this printing ×
 * cutting pair costs X") while negotiating per-material exceptions. The exception always wins.
 *
 * MODE "ALL": a key can hold in BOTH modes (INS cutting supplements) — it answers INCLUDED and SUPPLIED
 * alike.
 * @param l The quote line.
 * @param mode The PA mode (material included or supplied).
 * @param opts The volume context and the injected PA tables.
 * @returns The supplier PA and its notes, or null when no key matches.
 */
export function paSupplierLookup(l: QuoteLine, mode: PaMode, opts?: PaSupplierOptions): PaSupplierResult | null {
  const K = SERVICE_KEY[l.workshopResp ?? ""];
  if (!K) return null;
  const laminationCode = l.lamination || "";
  const pricingMode: PaMode = mode === "SUPPLIED" ? "SUPPLIED" : "INCLUDED";
  const cfCand = [l.finishing1, l.finishing2].find((c) => c != null && CF_PA_KEY.has(c)) || "";
  const cuts: string[] = l.cut != null ? [String(l.cut)] : [];
  if (l.cut && CUT_CLASS_PA.includes(l.cut) && l.cut !== "CUT-1501") cuts.push("CUT-1501");

  const grids = opts?.paGrids ?? PA_SUPPLIERS;
  const index = indexPaGrids(grids);
  const print = l.print ?? "";

  /**
   * First collected PA row matching the line for a finishing key and a material key.
   * @param finishingKey The priced finishing key ("" for none).
   * @param materialKey The material key ("" for a generic key).
   * @returns The matching row, or undefined.
   */
  const find = (finishingKey: string, materialKey: string) => {
    for (const cutCode of cuts) {
      const bucket = index.get(paIndexKey(K, materialKey, print, cutCode, laminationCode, finishingKey));
      if (!bucket) continue;
      const e = bucket.find(
        (x) => (x.mode === pricingMode || x.mode === "ALL") && isCollectedPaGrid(x.pa),
      );
      if (e) return e;
    }
    return undefined;
  };

  // Explicit first, generic next — at each level of finishing tolerance.
  // Lock §26.3: a PROVISIONAL material (-NEW) has by definition NO collected rate table, so a
  // workshop's GENERIC key does not cover it (no dedicated table = no declared capability, §23).
  const genericOpen = !isProvisionalRef(l.material);
  /**
   * Explicit material key first, generic key next.
   * @param finishingKey The priced finishing key ("" for none).
   * @returns The matching row, or undefined.
   */
  const search = (finishingKey: string) =>
    find(finishingKey, String(l.material ?? "")) ?? (genericOpen ? find(finishingKey, "") : undefined);

  let e = search(cfCand);
  let finishingIgnored = false;
  if (!e && cfCand) {
    e = search("");
    finishingIgnored = !!e;
  }
  if (!e) return null;
  return {
    pa: tierOf(opts, e.pa),
    note: e.note_idx ? (PA_NOTES[String(e.note_idx)] ?? "") : "",
    odillon: e.note_idx === 1,
    finishingIgnored,
    ...(e.min_flat_fee != null ? { min_flat_fee: e.min_flat_fee } : {}),
  };
}

// Prodhuit 1 m² minimum (minimum billing of provider P8)

/**
 * Group key for the P8 1 m² minimum: exact combination + mode = one order at Prodhuit.
 * @param l The quote line.
 * @param mode The PA mode.
 * @returns The group key.
 */
export function paGroupKeyP8(l: QuoteLine, mode: PaMode): string {
  return [
    l.material ?? "",
    l.print ?? "",
    l.cut ?? "",
    l.lamination ?? "",
    l.finishing1 ?? "",
    l.finishing2 ?? "",
    mode,
  ].join("|");
}

/**
 * Billing factor of the P8 1 m² minimum: when the cumulated m² of a combination bought at Prodhuit is
 * strictly below 1 m², P8 bills 1 m² (factor 1/cumulation, split pro rata); otherwise 1.
 * @param groupCumulatedM2 The cumulated m² of the purchase group.
 * @returns The billing factor.
 */
export function p8OneM2MinimumFactor(groupCumulatedM2: number): number {
  return groupCumulatedM2 > 0 && groupCumulatedM2 < 1 ? 1 / groupCumulatedM2 : 1;
}

/** What a purchase group consumed: its area and its workshop PA (excluding supplied material). */
export interface GroupCumulation {
  m2: number;
  /** Σ of the group's WORKSHOP PAs (printing+cutting only in SUPPLIED mode — never the material). */
  workshopPa: number;
  /** Flat fee € of the group's key (INS), if it carries one. */
  min_flat_fee?: number;
}

/** What the minimum imposes on the group, and why. */
export interface MinimumEffect {
  /** Multiplicative factor to apply to each group line's WORKSHOP PA (1 = nothing to do). */
  factor: number;
  /** The kind of floor that bit — null when none did. */
  type: "m2" | "euros" | null;
  /** Value of the applied floor (1 m², or the € flat fee) — for the tooltip. */
  value?: number;
}

/**
 * MINIMUM BILLING OF A PURCHASE GROUP (book §18).
 *
 * One group concept — workshop | material | printing | cutting | lamination | finishing | mode — and two
 * kinds of floor, chosen by the workshop's DATA (WORKSHOP_MINIMUMS), never by code:
 *  · "m2":    the group's billed area never drops below `value` (PRODHUIT: 1 m² per purchased
 *             combination — two different jobs = two setups = two minimums);
 *  · "euros": the group's total PA never drops below the flat fee carried by the key (INS).
 *
 * SUPPLIED mode: the floor is compared to the WORKSHOP PA ALONE (printing+cutting). The net material PA
 * is added ON TOP, never absorbed by the flat fee — the material comes from UPSIZE and the workshop does
 * not bill it.
 *
 * The factor is split pro rata across the group's lines (each keeps its share).
 * @param workshop The workshop name.
 * @param cumul What the purchase group consumed.
 * @returns The minimum effect on the group.
 */
export function groupMinimum(workshop: string, cumul: GroupCumulation): MinimumEffect {
  const min = WORKSHOP_MINIMUMS[SERVICE_KEY[workshop] ?? workshop];
  if (!min) return { factor: 1, type: null };

  if (min.type === "m2") {
    const threshold = min.value ?? 0;
    if (!(cumul.m2 > 0) || cumul.m2 >= threshold) return { factor: 1, type: null };
    return { factor: threshold / cumul.m2, type: "m2", value: threshold };
  }

  // euros: the flat fee comes from the key (byKey) or from the workshop.
  const threshold = min.byKey ? cumul.min_flat_fee : min.value;
  if (threshold == null || !(threshold > 0) || !(cumul.workshopPa > 0) || cumul.workshopPa >= threshold) {
    return { factor: 1, type: null };
  }
  return { factor: threshold / cumul.workshopPa, type: "euros", value: threshold };
}

/**
 * Total supplier PA of a line: PA €/m² × m² × 1 m² minimum factor.
 * @param servicePaM2 The supplier PA per m².
 * @param lineM2 The line area in m².
 * @param factor The minimum billing factor.
 * @returns The total supplier PA, rounded to the cent.
 */
export function servicePaTotal(servicePaM2: number, lineM2: number, factor: number): number {
  return round2(servicePaM2 * lineM2 * factor);
}
